import React, { Component } from 'react';
import _round from 'lodash/round';
import _isEqual from 'lodash/isEqual';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';

import { getTailwindColor } from '@trqk/utils';

import { unstyleScrollbar } from '../../helpers';

import { EarningsLegend } from '../EarningsLegend';

interface Props {
    trackSplits: TrackSplits[];
}

interface State {
    legendSources: EarningsLegendSource[];
}

const chartDivId = 'earnings-splits-per-track';
const legendDivId = 'earnings-splits-per-track-legend';
const xAxesCategory = 'work_title';
const valueAxisLabelColor = getTailwindColor('tertiary-400');

export class ChartLarge extends Component<Props, State> {
    private chart: am4charts.XYChart | null = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            legendSources: [],
        };
    }

    componentDidMount(): void {
        const { trackSplits } = this.props;

        const chart = am4core.create(chartDivId, am4charts.XYChart);

        chart.data = trackSplits;
        chart.numberFormatter.numberFormat = '$#,###.00##';

        const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());

        categoryAxis.dataFields.category = xAxesCategory;
        categoryAxis.renderer.grid.template.disabled = true;
        // Ensures that the first category label is shown
        categoryAxis.renderer.minGridDistance = 0.01;
        categoryAxis.renderer.labels.template.fullWords = false;
        categoryAxis.renderer.labels.template.horizontalCenter = 'right';
        categoryAxis.renderer.labels.template.maxWidth = 120;
        categoryAxis.renderer.labels.template.rotation = -90;
        // eslint-disable-next-line no-template-curly-in-string
        categoryAxis.renderer.labels.template.tooltipText = '{category}';
        categoryAxis.renderer.labels.template.tooltipX = am4core.percent(100);
        categoryAxis.renderer.labels.template.truncate = true;
        categoryAxis.renderer.labels.template.verticalCenter = 'middle';
        categoryAxis.fontSize = '0.8rem';

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());

        valueAxis.min = 0;
        valueAxis.renderer.labels.template.fill =
            am4core.color(valueAxisLabelColor);

        chart.scrollbarX = new am4core.Scrollbar();

        unstyleScrollbar(chart.scrollbarX);

        chart.zoomOutButton.disabled = true;

        chart.events.on('ready', (): void => {
            categoryAxis.zoomToIndexes(0, 10, false, true);
        });

        this.chart = chart;
        this.addSeriesToChart();
    }

    componentDidUpdate(prevProps: Props): void {
        const { trackSplits } = this.props;
        const { chart } = this;

        if (!_isEqual(prevProps.trackSplits, trackSplits) && chart !== null) {
            chart.data = trackSplits;
            this.addSeriesToChart();
        }
    }

    componentWillUnmount(): void {
        const { chart } = this;

        if (chart) {
            chart.dispose();
        }
    }

    createSeries({
        label,
        valueYKey,
    }: {
        label: string;
        valueYKey: string;
    }): am4charts.ColumnSeries | void {
        const { chart } = this;

        if (chart !== null) {
            const columnSeries = chart.series.push(
                new am4charts.ColumnSeries(),
            );

            columnSeries.name = label;
            columnSeries.dataFields.valueY = valueYKey;
            columnSeries.dataFields.categoryX = xAxesCategory;
            columnSeries.sequencedInterpolation = true;
            columnSeries.stacked = true;
            columnSeries.columns.template.width = am4core.percent(80);
            columnSeries.columns.template.tooltipText =
                // eslint-disable-next-line no-template-curly-in-string
                '[bold]{name}[/]\n[font-size:.75rem]{categoryX}: {valueY}';

            return columnSeries;
        }

        return undefined;
    }

    addSeriesToChart(): void {
        const { chart } = this;

        if (chart !== null && chart.data) {
            if (chart?.series) {
                chart.series.clear();
            }

            const legendSources = [] as EarningsLegendSource[];
            const { totalMemberEarnings, totalCollaboratorEarnings } =
                chart.data.reduce(
                    (
                        acc: TrackSplitsLegendTotals,
                        trackSplits: TrackSplits,
                    ) => {
                        if (trackSplits.earnings !== null) {
                            acc.totalMemberEarnings += trackSplits.earnings;
                        }

                        if (trackSplits.collaborator_earnings !== null) {
                            acc.totalCollaboratorEarnings +=
                                trackSplits.collaborator_earnings;
                        }

                        return acc;
                    },
                    {
                        totalMemberEarnings: 0,
                        totalCollaboratorEarnings: 0,
                    },
                );

            const memberLabel = 'You';
            const collaboratorLabel = 'Your collaborators';

            const memberColumnSeries = this.createSeries({
                label: memberLabel,
                valueYKey: 'earnings',
            });

            const formattedMemberLegendValue = `$${_round(
                totalMemberEarnings,
                2,
            ).toLocaleString('en')}`;

            if (memberColumnSeries) {
                legendSources.push({
                    columnSeries: memberColumnSeries,
                    legendValue: formattedMemberLegendValue,
                });
            }

            const collaboratorColumnSeries = this.createSeries({
                label: collaboratorLabel,
                valueYKey: 'collaborator_earnings',
            });

            const formattedCollaboratorLegendValue = `$${_round(
                totalCollaboratorEarnings,
                2,
            ).toLocaleString('en')}`;

            if (collaboratorColumnSeries) {
                legendSources.push({
                    columnSeries: collaboratorColumnSeries,
                    legendValue: formattedCollaboratorLegendValue,
                });
            }

            this.setState(() => ({ legendSources }));
        }
    }

    render(): JSX.Element {
        const { legendSources } = this.state;

        return (
            <div>
                <div
                    id={chartDivId}
                    style={{ width: '100%', height: '500px' }}
                />
                <EarningsLegend
                    addClasses="mt-6"
                    itemsPerRow={2}
                    legendDivId={legendDivId}
                    legendSources={legendSources}
                />
            </div>
        );
    }
}
